import { useMemo } from "react";
import { FileText, Notebook, Plus, Search } from "lucide-react";
import { WorkspaceDocument } from "../types";
import { collectVaultTags } from "../utils/notes";

interface NotesPanelProps {
  documents: WorkspaceDocument[];
  activeId: string;
  query: string;
  onQueryChange: (value: string) => void;
  onOpen: (id: string) => void;
  onNew: () => void;
}

export const NotesPanel = ({ documents, activeId, query, onQueryChange, onOpen, onNew }: NotesPanelProps) => {
  const tags = useMemo(() => collectVaultTags(documents).slice(0, 12), [documents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    // `#tag` searches note bodies for that tag; anything else matches name + body.
    return documents.filter((doc) => `${doc.name}\n${doc.content}`.toLowerCase().includes(q));
  }, [documents, query]);

  return (
    <aside className="panel notes-panel" aria-label="Notes">
      <div className="panel-title">
        <Notebook size={18} />
        <span>Notes</span>
        <span className="panel-count">{documents.length}</span>
        <button type="button" className="panel-action" title="New note" aria-label="New note" onClick={onNew}>
          <Plus size={15} />
        </button>
      </div>

      <div className="notes-search">
        <Search size={14} aria-hidden />
        <input
          type="text"
          value={query}
          placeholder="Search notes & #tags…"
          onChange={(event) => onQueryChange(event.target.value)}
          aria-label="Search notes"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {tags.length > 0 && (
        <div className="tag-row" aria-label="Tags in vault">
          {tags.map(({ tag, count }) => (
            <button
              key={tag}
              type="button"
              className={query.trim().toLowerCase() === `#${tag.toLowerCase()}` ? "tag-chip active" : "tag-chip"}
              title={`${count} note${count === 1 ? "" : "s"}`}
              onClick={() => onQueryChange(query.trim().toLowerCase() === `#${tag.toLowerCase()}` ? "" : `#${tag}`)}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="muted">No notes match “{query}”.</p>
      ) : (
        <nav className="notes-list">
          {filtered.map((doc) => (
            <button
              key={doc.id}
              type="button"
              className={doc.id === activeId ? "notes-item active" : "notes-item"}
              title={doc.name}
              onClick={() => onOpen(doc.id)}
            >
              <FileText size={14} aria-hidden />
              <span className="notes-item-name">{doc.name || "Untitled"}</span>
            </button>
          ))}
        </nav>
      )}
    </aside>
  );
};
